/**
 * Arcade games — Snake, Tetris, anything with gravity — and why they cannot be
 * built the way the other forty-three are.
 *
 * ## The wall
 *
 * Every existing game is turn-based and server-authoritative: a move goes to
 * the Durable Object, `apply` runs there, the new state comes back. Time-driven
 * games use `tick`/`nextTickAt`, which the room turns into a Durable Object
 * alarm — and `scheduleAlarm` floors every alarm at `now + 250ms`, on purpose,
 * because an alarm that can be scheduled in the past will re-fire as fast as
 * the runtime allows for as long as the room exists.
 *
 * That floor is a ceiling of four server ticks a second. Tetris gravity fits
 * under it. Snake at eight steps a second does not. Flappy Bird at sixty does
 * not come close, and never will: sixty alarms a second per room is not a thing
 * to attempt on any budget.
 *
 * ## The way round it
 *
 * Do not stream the world. Simulate it on both devices and check the answer.
 *
 * `packages/core` is already pure — `apply` takes no generator, every existing
 * game derives its randomness from a seed held in state, and the tests replay
 * whole games from a seed to a fixed outcome. That property was built for
 * testability, but it is exactly what deterministic replay needs, so the
 * expensive part of this design is already done and has been all along.
 *
 * So: the server issues a seed. Both clients run the identical `step` function
 * at a fixed rate, locally, at whatever frame rate the game wants, and record
 * what the player pressed on each step. When the run ends the client sends its
 * **input log** — not its score. The server replays the same `step` over the
 * same log and works the score out for itself.
 *
 * A modified client can therefore play *differently*, and can play better than
 * a human could, which is true of every game ever made and is not solvable
 * here. What it cannot do is claim a score that no sequence of legal inputs
 * produces. The number on the leaderboard is always one the server computed.
 *
 * ## What the other player sees
 *
 * Both play the same seed side by side, and the socket carries a progress
 * number a couple of times a second — nothing that needs to be in sync, and
 * nothing that breaks when a train goes into a tunnel. This is also the more
 * honest shape for these games: two snakes sharing one grid over a network is a
 * latency problem dressed up as a feature, and racing the same maze is what
 * children actually do with them anyway.
 */

/** How many simulation steps a second. Fixed — never derived from a clock. */
export type StepRate = 8 | 12 | 30 | 60;

export interface RealtimeState {
	/** Steps simulated so far. The only clock a realtime game may read. */
	step: number;
	/** Fixed at the start by the server, and the same for both players. */
	seed: number;
	/** Set once the run is over. Nothing may change after it is set. */
	over: boolean;
	/** What the server will compare. Higher is better, always. */
	score: number;
}

/**
 * One player's run: a pure state machine advanced a fixed step at a time.
 *
 * Deliberately not a `GameDefinition`. That contract is about two players
 * taking turns against one shared state, and forcing this into it would mean
 * `player` arguments that are always the same value and a `now` that must never
 * be read. Two honest shapes beat one dishonest one.
 */
export interface RealtimeGame<S extends RealtimeState, I> {
	id: string;
	/** Fixed timestep. `step(state, input)` advances exactly one of these. */
	rate: StepRate;
	/** How long a run may last, in steps. Bounds replay cost on the server. */
	maxSteps: number;
	/**
	 * How many presses may wait their turn, if not the default.
	 *
	 * A snake wants very few: one input is one quarter turn, and a child drumming
	 * on the button should not find themselves steering something that is
	 * carrying out orders from two seconds ago. A game steered by dragging wants
	 * many, because one drag across the board is a dozen presses made at once and
	 * dropping ten of them means the piece stops halfway to the finger.
	 */
	maxQueued?: number;

	create(seed: number): S;

	/** Validates one input off the wire. `null` means "not an input". */
	parseInput(raw: unknown): I | null;

	/**
	 * Advances exactly one step. Pure: no clock, no generator, no I/O.
	 *
	 * `input` is what the player pressed during this step, or null. Both the
	 * browser and the Durable Object run this, and they must agree exactly —
	 * so it may not use floating point in a way that could differ, and it may
	 * not read anything that is not in `state`.
	 */
	step(state: S, input: I | null): S;
}

/** One press, pinned to the step it happened on. */
export interface InputEvent<I> {
	at: number;
	input: I;
}

export type ReplayVerdict =
	| { ok: true; score: number; steps: number; survived: boolean }
	| {
			ok: false;
			reason: "out-of-order" | "step-out-of-range" | "bad-input" | "too-many-inputs";
	  };

/**
 * Replays a client's input log and returns the score the server believes.
 *
 * Everything here is a bound on work rather than a rule of the game: the log
 * arrives from a browser, and a browser is allowed to be hostile even when
 * ninety-nine times in a hundred it is a nine-year-old on an iPad.
 */
export function verifyRun<S extends RealtimeState, I>(
	game: RealtimeGame<S, I>,
	seed: number,
	log: readonly InputEvent<I>[],
): ReplayVerdict {
	// One input per step is the most a player can produce, so a longer log is
	// not a close call — it is a log that was not produced by playing.
	if (log.length > game.maxSteps) return { ok: false, reason: "too-many-inputs" };

	/*
	 * Every press is put through the game's own parser before it is replayed.
	 *
	 * This was missing at first, and for the three games there are it happened
	 * not to matter: every malformed input any of them can be handed behaves
	 * exactly like some legal one, so nothing unreachable could be scored. That
	 * is luck rather than design. A later game whose input carries a *number* —
	 * a magnitude, a column, an index — would be handed one straight off the
	 * wire, and the guarantee this whole file exists for would go, with nothing
	 * failing to say so. The contract advertises `parseInput` as the validation
	 * boundary, so the boundary has to be here.
	 */
	const presses: (InputEvent<I> | null)[] = [];
	let previous = -1;
	for (const event of log) {
		if (!Number.isInteger(event.at)) return { ok: false, reason: "bad-input" };
		if (event.at <= previous) return { ok: false, reason: "out-of-order" };
		if (event.at < 0 || event.at >= game.maxSteps)
			return { ok: false, reason: "step-out-of-range" };
		const input = game.parseInput(event.input);
		if (input === null) return { ok: false, reason: "bad-input" };
		presses.push({ at: event.at, input });
		previous = event.at;
	}

	let state = game.create(seed);
	let next = 0;

	while (!state.over && state.step < game.maxSteps) {
		const press =
			next < presses.length && presses[next]?.at === state.step ? presses[next++] : null;
		state = game.step(state, press ? press.input : null);
	}

	/*
	 * Reaching the ceiling is a way of finishing, not a way of cheating.
	 *
	 * This first rejected such a run, on the reasoning that a log which never
	 * dies could bank the maximum. A test with a policy that circles forever
	 * showed that up for what it was: surviving three minutes is the *skill* in
	 * a game like this, and refusing to score it punishes precisely the player
	 * who played best. There is nothing to bank anyway — the score comes from
	 * apples, and going round in a square eats none of them.
	 */
	return { ok: true, score: state.score, steps: state.step, survived: !state.over };
}

/**
 * Plays a run to its end with a policy rather than a log — the same loop the
 * browser runs, and what the tests and the computer opponent use.
 */
export function playRun<S extends RealtimeState, I>(
	game: RealtimeGame<S, I>,
	seed: number,
	choose: (state: S) => I | null,
): { state: S; log: InputEvent<I>[] } {
	let state = game.create(seed);
	const log: InputEvent<I>[] = [];

	while (!state.over && state.step < game.maxSteps) {
		const input = choose(state);
		if (input !== null) log.push({ at: state.step, input });
		state = game.step(state, input);
	}
	return { state, log };
}

// --- The loop a device actually runs ---------------------------------------

/**
 * How much simulation one animation frame may do.
 *
 * A backgrounded tab stops getting frames, so the first frame after coming
 * back reports however long the child was reading a message instead. Without
 * this the snake would take a hundred steps in one go and hit a wall before
 * anybody could touch anything.
 *
 * That does mean switching tabs is a pause, and there is no way round it: the
 * log carries step numbers and no wall-clock, so the server cannot tell a
 * paused run from a fast one and pretending otherwise would only add a rule
 * nothing enforces. Thinking time is free here, and banking it earns nothing —
 * the score comes from apples, and a paused snake eats none.
 */
const MAX_CATCHUP_STEPS = 4;

/**
 * How many presses may wait their turn, unless a game asks for more.
 *
 * One step is consumed per input, so a queue is what makes two quick taps both
 * count rather than the second replacing the first. Short by default: a child
 * drumming on a button should not find themselves steering something that
 * carries out orders from two seconds ago.
 */
const DEFAULT_MAX_QUEUED = 3;

/** A run in progress on a device, between frames. */
export interface RunProgress<S extends RealtimeState, I> {
	state: S;
	/** What will be sent up at the end, built as it goes. */
	log: InputEvent<I>[];
	/** Elapsed time not yet turned into steps, in milliseconds. */
	owed: number;
	/** Presses waiting for a step to happen on. */
	queue: I[];
}

export function startRun<S extends RealtimeState, I>(
	game: RealtimeGame<S, I>,
	seed: number,
): RunProgress<S, I> {
	return { state: game.create(seed), log: [], owed: 0, queue: [] };
}

export function runFinished<S extends RealtimeState, I>(
	game: RealtimeGame<S, I>,
	progress: RunProgress<S, I>,
): boolean {
	return progress.state.over || progress.state.step >= game.maxSteps;
}

/** Notes a press down to be applied on one of the next few steps. */
export function queueInput<S extends RealtimeState, I>(
	game: RealtimeGame<S, I>,
	progress: RunProgress<S, I>,
	input: I,
): RunProgress<S, I> {
	if (progress.queue.length >= (game.maxQueued ?? DEFAULT_MAX_QUEUED)) return progress;
	return { ...progress, queue: [...progress.queue, input] };
}

/**
 * Advances a run by however long has passed since the last frame.
 *
 * This is `playRun`'s loop with a clock bolted on the outside, and it has to
 * stay that way: the server replays the log with `verifyRun`, which applies an
 * input on exactly the step its `at` says. Recording the step *before* calling
 * `step` is what keeps the two in step, so to speak.
 *
 * The step length is a float at sixty steps a second and that is fine — it only
 * decides when a frame does its work locally. Nothing in the log is a time.
 */
export function advanceRun<S extends RealtimeState, I>(
	game: RealtimeGame<S, I>,
	progress: RunProgress<S, I>,
	elapsedMs: number,
): RunProgress<S, I> {
	const stepMs = 1000 / game.rate;
	let { state } = progress;
	let queue = progress.queue;
	let owed = Math.min(progress.owed + Math.max(elapsedMs, 0), stepMs * MAX_CATCHUP_STEPS);
	let log = progress.log;

	while (owed >= stepMs && !state.over && state.step < game.maxSteps) {
		owed -= stepMs;
		const input = queue.length > 0 ? queue[0] : null;
		if (input !== null) {
			// Copied rather than pushed: the caller holds the previous progress
			// while React decides whether anything changed.
			log = [...log, { at: state.step, input }];
			queue = queue.slice(1);
		}
		state = game.step(state, input);
	}

	return { state, log, owed, queue };
}
